/**
 * Google Photos Picker Service using Backend Proxy
 * This service now calls our backend proxy endpoints instead of Google Photos API directly
 * to avoid CORS issues and handle authentication securely.
 */

import authService from './authService';
import { getGoogleClientId, getGoogleClientIdDiagnostics, getGooglePhotosApiUrl } from '../config/runtimeConfig';

class GooglePhotosService {
  constructor() {
    this.isInitialized = false;
    this.tokenClient = null;
    this._accessToken = null; // Private property for access token
    this.debug = true;
    this.currentSession = null;
    this.pollingInterval = null;
    this.clientId = getGoogleClientId();
    
    // Required scope for Google Photos Picker API only
    // Note: photoslibrary.readonly was removed as Google deprecated broad Library API scopes after March 2025.
    // Creation time metadata is now sourced from the Picker API's createTime field and EXIF data extraction.
    this.scope = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly';
    
    // Backend proxy URLs
    this.API_BASE_URL = getGooglePhotosApiUrl();
    const diagnostics = getGoogleClientIdDiagnostics();
    
    this.log('GooglePhotosService initialized with backend proxy');
    this.log('Client ID configured:', this.clientId ? 'YES' : 'NO');
    this.log('Google Client ID diagnostics at service construction:', diagnostics);
    this.log('Backend proxy URL:', this.API_BASE_URL);
  }

  // Getter for access token so components can use it
  get accessToken() {
    return this._accessToken;
  }

  log(message, data = null) {
    if (this.debug) {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`[${timestamp}] [GooglePhotosService] ${message}`, data || '');
    }
  }

  async initialize() {
    this.log('Initializing Google Photos Picker API...');
    
    if (!this.clientId) {
      const diagnostics = getGoogleClientIdDiagnostics();
      this.log('Google Client ID diagnostics', diagnostics);
      throw new Error(
        `Google Client ID not configured. Resolved source: ${diagnostics.source}. ` +
        'Local dev expects REACT_APP_GOOGLE_CLIENT_ID in frontend/.env and a restarted frontend dev server. ' +
        'Deployed frontend expects REACT_APP_GOOGLE_CLIENT_ID or GOOGLE_CLIENT_ID in the container runtime environment so runtime-config.js can expose it.'
      );
    }

    return new Promise((resolve, reject) => {
      try {
        // Check if Google Identity Services is already loaded
        if (window.google?.accounts?.oauth2) {
          this.log('Google Identity Services already loaded');
          this.initializeAuth();
          resolve();
          return;
        }

        // Load Google Identity Services
        this.log('Loading Google Identity Services...');
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        
        script.onload = () => {
          this.log('Google Identity Services loaded successfully');
          this.initializeAuth();
          resolve();
        };
        
        script.onerror = (error) => {
          this.log('Failed to load Google Identity Services:', error);
          reject(new Error('Failed to load Google Identity Services'));
        };
        
        document.head.appendChild(script);
      } catch (error) {
        this.log('Initialization error:', error);
        reject(error);
      }
    });
  }

  initializeAuth() {
    this.log('Initializing OAuth token client...');
    
    try {
      // Initialize token client for Google Photos Picker API
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: this.clientId,
        scope: this.scope,
        callback: (response) => {
          this.log('OAuth callback received:', response);
          if (response.error) {
            this.log('OAuth error:', response);
            return;
          }
          this._accessToken = response.access_token;
          this.log('Access token obtained successfully');
        }
      });

      this.isInitialized = true;
      this.log('OAuth token client initialized successfully');
    } catch (error) {
      this.log('Auth initialization error:', error);
      throw error;
    }
  }

  async requestAccess() {
    this.log('Requesting access token...');
    
    if (!this.tokenClient) {
      throw new Error('Auth not initialized. Call initialize() first.');
    }

    return new Promise((resolve, reject) => {
      try {
        // Update callback to resolve promise
        this.tokenClient.callback = (response) => {
          this.log('Access token response:', response);
          if (response.error) {
            this.log('Token request error:', response);
            reject(new Error(`Token request failed: ${response.error}`));
            return;
          }
          this._accessToken = response.access_token;
          this.log('Access token obtained:', this._accessToken ? 'YES' : 'NO');
          resolve();
        };

        // Request access token with consent prompt
        this.tokenClient.requestAccessToken({ prompt: 'consent' });
      } catch (error) {
        this.log('Token request error:', error);
        reject(error);
      }
    });
  }

  async createSession() {
    this.log('Creating Google Photos Picker session via backend proxy...');
    
    if (!this._accessToken) {
      throw new Error('No access token available. Call requestAccess() first.');
    }

    try {
      this.log('Making session create request to backend proxy...');
      
      // Get the app's auth token for backend requests
      const appAuthToken = authService.getToken();
      if (!appAuthToken) {
        throw new Error('Not authenticated with the app. Please log in.');
      }

      const response = await fetch(`${this.API_BASE_URL}/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${appAuthToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessToken: this._accessToken
        })
      });

      this.log('Backend session create response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        this.log('Backend session create error response:', errorData);
        throw new Error(`Failed to create session: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      this.log('Session created successfully via backend:', result);
      
      if (result.success && result.session) {
        this.currentSession = result.session;
        return result.session;
      } else {
        throw new Error('Invalid response from backend');
      }
    } catch (error) {
      this.log('Session creation error:', error);
      throw error;
    }
  }

  async pollSession(sessionId) {
    this.log(`Polling session via backend proxy: ${sessionId}`);
    
    if (!this._accessToken) {
      throw new Error('No access token available');
    }

    try {
      // Get the app's auth token for backend requests
      const appAuthToken = authService.getToken();
      if (!appAuthToken) {
        throw new Error('Not authenticated with the app. Please log in.');
      }

      const response = await fetch(`${this.API_BASE_URL}/sessions/${sessionId}?accessToken=${encodeURIComponent(this._accessToken)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${appAuthToken}`
        }
      });

      this.log('Backend session poll response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        this.log('Backend session poll error response:', errorData);
        throw new Error(`Failed to poll session: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      this.log('Session poll result via backend:', result);
      
      if (result.success && result.session) {
        return result.session;
      } else {
        throw new Error('Invalid response from backend');
      }
    } catch (error) {
      this.log('Session polling error:', error);
      throw error;
    }
  }

  async getSelectedMediaItems(sessionId, userSessionId = null) {
    this.log(`Getting selected media items via backend proxy for session: ${sessionId}`);
    
    if (!this._accessToken) {
      throw new Error('No access token available');
    }

    try {
      // Get the app's auth token for backend requests
      const appAuthToken = authService.getToken();
      if (!appAuthToken) {
        throw new Error('Not authenticated with the app. Please log in.');
      }

      const response = await fetch(`${this.API_BASE_URL}/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${appAuthToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessToken: this._accessToken,
          sessionId: sessionId,
          userSessionId: userSessionId
        })
      });

      this.log('Backend media items response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        this.log('Backend media items error response:', errorData);
        throw new Error(`Failed to get media items: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      this.log('📸 Media items retrieved via backend:', result);
      
      // Enhanced debugging - log the full structure
      this.log('🔍 Backend response keys:', Object.keys(result));
      this.log('🔍 Result.success:', result.success);
      this.log('🔍 Result.mediaItems length:', result.mediaItems?.length || 0);
      
      if (result.session) {
        this.log('🔍 Full session object keys:', Object.keys(result.session));
        this.log('🔍 Session status:', result.session.status);
        this.log('🔍 Session mediaItemsSet:', result.session.mediaItemsSet);
        this.log('🔍 Session data (first 1000 chars):', JSON.stringify(result.session).substring(0, 1000));
        
        // Check for various property names in session
        const possibleMediaProps = [
          'mediaItems', 'mediaItemResults', 'pickedMediaItems', 
          'selectedMediaItems', 'items', 'results'
        ];
        
        possibleMediaProps.forEach(prop => {
          if (result.session[prop]) {
            this.log(`🔍 Found media in session.${prop}:`, result.session[prop].length, 'items');
            if (result.session[prop].length > 0) {
              this.log(`🔍 First item in session.${prop}:`, JSON.stringify(result.session[prop][0]).substring(0, 300));
            }
          }
        });
      }
      
      // Check for media items directly in result
      if (result.mediaItems && result.mediaItems.length > 0) {
        this.log('📸 Found media items in result:', result.mediaItems.length, 'items');
        this.log('📸 First media item:', JSON.stringify(result.mediaItems[0]).substring(0, 300));
      } else {
        this.log('⚠️ No media items found in result');
      }
      
      // Check for uploaded files
      if (result.uploadedFiles && result.uploadedFiles.length > 0) {
        this.log('🎉 Found uploaded files in result:', result.uploadedFiles.length, 'files');
        this.log('🎉 First uploaded file:', JSON.stringify(result.uploadedFiles[0]).substring(0, 300));
      } else {
        this.log('⚠️ No uploaded files found in result');
      }
      
      if (result.success) {
        return {
          mediaItems: result.mediaItems || [],
          uploadedFiles: result.uploadedFiles || [],
          nextPageToken: result.nextPageToken
        };
      } else {
        throw new Error('Invalid response from backend');
      }
    } catch (error) {
      this.log('Media items error:', error);
      throw error;
    }
  }

  async testBackendConnection() {
    this.log('Testing backend Google Photos API connection...');
    
    if (!this._accessToken) {
      throw new Error('No access token available');
    }

    try {
      // Get the app's auth token for backend requests
      const appAuthToken = authService.getToken();
      if (!appAuthToken) {
        throw new Error('Not authenticated with the app. Please log in.');
      }

      const response = await fetch(`${this.API_BASE_URL}/test?accessToken=${encodeURIComponent(this._accessToken)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${appAuthToken}`
        }
      });

      this.log('Backend test response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        this.log('Backend test error response:', errorData);
        throw new Error(`Backend test failed: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      this.log('Backend test result:', result);
      
      return result;
    } catch (error) {
      this.log('Backend test error:', error);
      throw error;
    }
  }

  async startPickerFlow(userSessionId = null) {
    this.log('Starting Google Photos Picker flow...');
    
    try {
      // Step 1: Open a placeholder window immediately to avoid popup blockers
      this.log('Opening placeholder picker window...');
      const pickerWindow = window.open('about:blank', '_blank', 'width=800,height=600');
      
      if (!pickerWindow) {
        throw new Error('Failed to open picker window. Please allow popups and try again.');
      }
      
      // Step 2: Ensure we have initialized
      if (!this.isInitialized) {
        await this.initialize();
      }

      // Step 3: Get access token
      if (!this._accessToken) {
        await this.requestAccess();
      }

      // Step 4: Create session
      const session = await this.createSession();
      
      // Step 5: Navigate the existing window to the picker URI
      if (session.pickerUri) {
        this.log('Opening picker URI:', session.pickerUri);
        
        // Navigate the placeholder window to the actual picker URI
        pickerWindow.location.href = session.pickerUri;
        
        // Step 6: Start polling for results
        return this.startPolling(session.id, userSessionId);
      } else {
        pickerWindow.close();
        throw new Error('No picker URI received from session');
      }
    } catch (error) {
      this.log('Picker flow error:', error);
      throw error;
    }
  }

  async startPolling(sessionId, userSessionId = null) {
    this.log(`Starting polling for session: ${sessionId}`);
    
    return new Promise((resolve, reject) => {
      let pollCount = 0;
      const maxPolls = 150; // 5 minutes with 2-second intervals
      
      const poll = async () => {
        try {
          pollCount++;
          this.log(`Poll attempt ${pollCount}/${maxPolls}`);
          
          const sessionData = await this.pollSession(sessionId);
          
          if (sessionData.mediaItemsSet) {
            this.log('✅ User has selected photos! Getting media items...');
            clearInterval(this.pollingInterval);
            
            // Get the selected media items
            const mediaItems = await this.getSelectedMediaItems(sessionId, userSessionId);
            
            // Clean up session
            await this.deleteSession(sessionId);
            
            resolve(mediaItems);
          } else {
            this.log('⏳ Waiting for user to select photos...');
            
            // Check for timeout
            if (pollCount >= maxPolls) {
              this.log('❌ Polling timeout reached');
              clearInterval(this.pollingInterval);
              await this.deleteSession(sessionId);
              reject(new Error('Picker session timed out after 5 minutes'));
            }
          }
        } catch (error) {
          this.log('Polling error:', error);
          clearInterval(this.pollingInterval);
          
          // Try to clean up session
          try {
            await this.deleteSession(sessionId);
          } catch (cleanupError) {
            this.log('Session cleanup error:', cleanupError);
          }
          
          reject(error);
        }
      };

      // Start immediate poll
      poll();
      
      // Continue polling every 2 seconds
      this.pollingInterval = setInterval(poll, 2000);
    });
  }

  async deleteSession(sessionId) {
    this.log('Deleting picker session:', sessionId);
    
    try {
      const appAuthToken = authService.getToken();
      if (!appAuthToken) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.API_BASE_URL}/session/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${appAuthToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to delete session: ${response.statusText}`);
      }

      this.log('Session deleted successfully:', sessionId);
      return true;
    } catch (error) {
      this.log('Error deleting session:', error);
      // Don't throw error as this is cleanup - just log it
      return false;
    }
  }

  async cleanup() {
    this.log('Cleaning up Google Photos service...');
    
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    // Clear current session (no need to delete via API for Picker)
    if (this.currentSession) {
      this.log('Clearing current session:', this.currentSession.id);
      this.currentSession = null;
    }
  }

  clearAuth() {
    this.log('Clearing authentication...');
    this._accessToken = null;
    this.tokenClient = null;
    this.isInitialized = false;
    this.cleanup();
  }

  // Legacy compatibility method
  async importPhotos() {
    this.log('importPhotos() called - redirecting to startPickerFlow()');
    return this.startPickerFlow();
  }

  // Debug method to check auth status
  getAuthStatus() {
    return {
      isInitialized: this.isInitialized,
      hasAccessToken: !!this._accessToken,
      hasTokenClient: !!this.tokenClient,
      currentSession: this.currentSession?.id || null
    };
  }
}

export default new GooglePhotosService();
